import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Check, Sparkles, LogIn, Pencil } from 'lucide-react';
import { usePublicProfile, ProfileRole } from '../context/PublicProfileContext';
export function PublicProfilePage({
  role
}: {
  role: ProfileRole;
}) {
  const {
    profile
  } = usePublicProfile(role);
  const editorPath = `/${role}/pagina-pubblica`;
  const initials = profile.businessName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w.charAt(0)).join('').toUpperCase();
  return <div className="min-h-screen w-full bg-gray-50" data-id="element-3733">
      {/* Public top bar: CleanAI brand (left) + access invite (right) */}
      <header className="w-full bg-white border-b border-border sticky top-0 z-10" data-id="element-3734">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3" data-id="element-3735">
          <span className="inline-flex items-center gap-2" data-id="element-3736">
            <span className="w-8 h-8 rounded-lg bg-coral flex items-center justify-center" data-id="element-3737">
              <Sparkles size={17} className="text-white" data-id="element-3738" />
            </span>
            <span className="text-lg font-bold text-navy" data-id="element-3739">
              Clean<span className="text-coral" data-id="element-3740">AI</span>
            </span>
          </span>
          <Link to="/login" className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90 transition-colors" data-id="element-3741">
            <LogIn size={16} data-id="element-3742" />
            Accedi alla piattaforma
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto bg-white sm:my-6 sm:rounded-2xl sm:shadow-sm sm:border sm:border-border overflow-hidden" data-id="element-3743">
        {/* Cover */}
        <div className="relative h-44 sm:h-60 w-full bg-gray-100" data-id="element-3744">
          {profile.coverUrl ? <img src={profile.coverUrl} alt={`Copertina ${profile.businessName}`} className="w-full h-full object-cover" data-id="element-3745" /> : <div className="w-full h-full bg-navy/10" data-id="element-3746" />}
        </div>

        {/* Header: logo sits BELOW the cover */}
        <div className="px-5 sm:px-8 pt-6 pb-6 border-b border-border" data-id="element-3747">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-coral shadow-sm flex-shrink-0 flex items-center justify-center" data-id="element-3748">
            {profile.logoUrl ? <img src={profile.logoUrl} alt={`Logo ${profile.businessName}`} className="w-full h-full object-cover" data-id="element-3749" /> : <span className="text-white font-extrabold text-2xl tracking-tight" data-id="element-3750">
                {initials}
              </span>}
          </div>
          <div className="mt-4" data-id="element-3751">
            <h1 className="text-2xl font-bold text-navy" data-id="element-3752">
              {profile.businessName}
            </h1>
            {profile.tagline && <p className="text-sm text-muted-foreground mt-1" data-id="element-3753">
                {profile.tagline}
              </p>}
            {profile.zones.length > 0 && <div className="flex items-center gap-1.5 mt-3 text-sm text-navy" data-id="element-3754">
                <MapPin size={15} className="text-coral flex-shrink-0" data-id="element-3755" />
                <span className="text-muted-foreground" data-id="element-3756">
                  {profile.zones.join(' · ')}
                </span>
              </div>}
          </div>
        </div>

        {/* Description */}
        {profile.description && <section className="px-5 sm:px-8 py-6 border-b border-border" data-id="element-3757">
            <h2 className="text-sm font-semibold text-navy mb-2" data-id="element-3758">Chi siamo</h2>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line" data-id="element-3759">
              {profile.description}
            </p>
          </section>}

        {/* Services */}
        {profile.services.length > 0 && <section className="px-5 sm:px-8 py-6 border-b border-border" data-id="element-3760">
            <h2 className="text-sm font-semibold text-navy mb-3" data-id="element-3761">
              Servizi offerti
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-3762">
              {profile.services.map(s => <div key={s} className="flex items-center gap-2.5 bg-gray-50 border border-border rounded-lg px-3 py-2.5" data-id="element-3763">
                  <span className="w-6 h-6 rounded-full bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-3764">
                    <Check size={14} className="text-coral" data-id="element-3765" />
                  </span>
                  <span className="text-sm font-medium text-foreground" data-id="element-3766">
                    {s}
                  </span>
                </div>)}
            </div>
          </section>}

        {/* CTA — requests are handled through CleanAI (no direct contacts) */}
        <section className="px-5 sm:px-8 py-6" data-id="element-3767">
          <div className="rounded-xl bg-gray-50 border border-border p-5 text-center" data-id="element-3768">
            <h2 className="text-base font-bold text-navy" data-id="element-3769">
              Vuoi lavorare con {profile.businessName}?
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto" data-id="element-3770">
              Le richieste vengono gestite in modo sicuro tramite CleanAI.
              Accedi alla piattaforma per inviare la tua richiesta.
            </p>
            <Link to="/login" className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors" data-id="element-3771">
              <Sparkles size={16} data-id="element-3772" />
              {profile.ctaLabel || 'Richiedi tramite CleanAI'}
            </Link>
          </div>
        </section>
      </main>

      {/* Discreet owner shortcut back to the editor */}
      <Link to={editorPath} className="fixed bottom-4 right-4 inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-border rounded-full shadow-md text-xs font-semibold text-navy hover:border-coral hover:text-coral transition-colors" data-id="element-3773">
        <Pencil size={13} data-id="element-3774" />
        Modifica
      </Link>
    </div>;
}